// Entity Management Types

export enum EntityType {
  XERO = "xero",
  QBO = "qbo",
  MANUAL = "manual"
}

export enum EntityStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  ERROR = "error",
  SYNCING = "syncing",
  PENDING = "pending",
  DISCONNECTED = "disconnected"
}


export interface EntityConnectionDetails {
  status: EntityStatus;
  last_checked?: string;
  last_sync?: string;
  error_message?: string;
  oauth_expires_at?: string;
  tenant_id?: string;
  organization_id?: string;
}

// Compliance threshold configuration (firm_admin only)
export interface ComplianceThresholds {
  aged_payables_days?: number;  // Range: 1-180, default: 30
  aged_receivables_days?: number;  // Range: 1-180, default: 30
  serial_checker_max_gap?: number;  // Range: ≥1, default: 50
  serial_checker_inversion_limit?: number;  // Range: ≥1, default: 5000
  // Type K: Low Cost Fixed Assets - capitalization threshold (default: 1000)
  low_cost_asset_threshold?: number;
  // Type L/M: Old Credits - days threshold for flagging old credits (default: 90)
  old_credit_days?: number;
  // Type N: Misallocated Items - amount threshold (default: 10)
  misallocation_threshold?: number;
  // Type N: Misallocated Items - list of catch-all account codes to flag
  misallocation_accounts?: string[];
}

// AML threshold configuration (firm_admin only)
export interface AmlThresholds {
  // Master toggle
  enabled?: boolean;  // Whether AML detection is enabled (default: false for new entities)

  // Detection Thresholds
  new_supplier_threshold?: number;  // Type K - Amount threshold for new supplier detection (default: 1000)
  new_customer_threshold?: number;  // Type K - Amount threshold for new customer detection (default: 3000)
  count_spike_multiplier?: number;  // Type L - Transaction count spike detection multiplier (default: 1.5)
  volume_spike_multiplier?: number;  // Type O - Transaction volume spike detection multiplier (default: 3.0)
  soft_gl_amount_threshold?: number;  // Type M - Soft GL coding amount threshold (default: 5000)
  attachment_threshold?: number;  // Type N - Missing attachment amount threshold (default: 500)
  cn_ratio_threshold?: number;  // Type R - Credit note ratio threshold (default: 0.15 = 15%)
  round_number_threshold?: number;  // Type S - Round number amount threshold (default: 5000)
  backdate_days_threshold?: number;  // Type T - Backdating days threshold (default: 3)
  pass_through_threshold?: number;  // Type Q - Minimum amount for pass-through/conduit detection (default: 1000)
  pass_through_days?: number;  // Type Q - Time window for pass-through detection (default: 7 days)

  // Workflow Settings
  claim_duration_hours?: number;  // Claim lock duration in hours (default: 24)
  max_snooze_days?: number;  // Maximum snooze duration in days (default: 90)
  max_resubmits?: number;  // Maximum resubmit attempts (default: 5)
}

// Validation constants for compliance thresholds
export const COMPLIANCE_THRESHOLD_LIMITS = {
  agedPayables: { min: 1, max: 180, default: 30 },
  agedReceivables: { min: 1, max: 180, default: 30 },
  serialCheckerMaxGap: { min: 1, default: 50 },
  serialCheckerInversionLimit: { min: 1, default: 5000 },
  // Type K: Low Cost Fixed Assets
  lowCostAssetThreshold: { min: 0, default: 1000 },
  // Type L/M: Old Credits
  oldCreditDays: { min: 1, max: 365, default: 90 },
  // Type N: Misallocated Items
  misallocationThreshold: { min: 0, default: 10 },
  misallocationAccountsMax: 50  // Maximum number of accounts
} as const;

// Validation constants for AML thresholds
export const AML_THRESHOLD_LIMITS = {
  // Detection Thresholds
  newSupplierThreshold: { min: 0, default: 1000 },
  newCustomerThreshold: { min: 0, default: 3000 },
  countSpikeMultiplier: { min: 1.0, default: 1.5 },
  volumeSpikeMultiplier: { min: 1.0, default: 3.0 },
  softGlAmountThreshold: { min: 0, default: 5000 },
  attachmentThreshold: { min: 0, default: 500 },
  cnRatioThreshold: { min: 0, max: 1, default: 0.15 },  // Type R - Credit note ratio (0.15 = 15%)
  roundNumberThreshold: { min: 0, default: 5000 },
  backdateDaysThreshold: { min: 0, default: 14 },
  passThroughThreshold: { min: 0, default: 1000 },  // Type Q - Minimum amount for pass-through detection

  // Workflow Settings
  claimDurationHours: { min: 1, max: 168, default: 24 },  // 1 hour to 1 week
  maxSnoozeDays: { min: 1, max: 365, default: 90 },
  maxResubmits: { min: 1, max: 10, default: 5 }
} as const;

export interface EntitySettings {
  entity_id: string;
  entity_name: string;
  prepayment_asset_account_codes: string[];
  xero_short_code?: string;
  excluded_pnl_account_codes?: string[];
  default_expense_account_code?: string;
  default_amortization_months?: number;
  amortization_materiality_threshold?: number;
  amortization_apply_50_percent_rule?: boolean;
  auto_sync_enabled?: boolean;
  sync_frequency?: 'hourly' | 'daily' | 'weekly' | 'manual';
  sync_spend_money?: boolean;
  // Transaction sync settings
  transaction_sync_start_date?: string;
  // Individual sync data type toggles - DEPRECATED: System now automatically syncs all data types
  /** @deprecated No longer user-configurable - system automatically syncs all data types for integrity */
  sync_invoices?: boolean;
  /** @deprecated No longer user-configurable - system automatically syncs all data types for integrity */
  sync_bills?: boolean;
  /** @deprecated No longer user-configurable - system automatically syncs all data types for integrity */
  sync_payments?: boolean;
  /** @deprecated No longer user-configurable - system automatically syncs all data types for integrity */
  sync_bank_transactions?: boolean;
  /** @deprecated No longer user-configurable - system automatically syncs all data types for integrity */
  sync_journal_entries?: boolean;
  // Additional settings
  auto_post_proposed_journals?: boolean;
  ai_scanning_enabled?: boolean; // AI scanning toggle – aligns with wizard
  universal_ocr_enabled?: boolean; // Phase 1: OCR all attachments (not just prepayment-flagged)
  email_forwarding?: {
    enabled: boolean;
    forward_to_email?: string | null;
    max_forwards_per_hour?: number;
  };
  base_currency_code?: string;
  initial_sync_completed?: boolean;
  last_full_sync_date?: string;
  notification_preferences?: {
    sync_errors: boolean;
    connection_issues: boolean;
    data_anomalies: boolean;
  };
  // Compliance threshold configuration
  compliance_thresholds?: ComplianceThresholds;
  // AML threshold configuration
  aml_thresholds?: AmlThresholds;
}

export interface EntityHealthMetrics {
  connection_uptime: number; // percentage
  last_successful_sync?: string;
  sync_error_count: number;
  data_quality_score: number; // 0-100
  pending_items_count: number;
  error_items_count: number;
}

export interface EntityCreate {
  client_id: string;
  entity_name: string;
  type: EntityType;
  description?: string;
  settings?: Partial<EntitySettings>;
}

export interface EntityUpdate {
  entity_name?: string;
  description?: string;
  status?: EntityStatus;
  tag_ids?: string[];
  settings?: Partial<EntitySettings>;
}

export interface EntityResponse {
  entity_id: string;
  client_id: string;
  entity_name: string;
  type: EntityType;
  status: EntityStatus;
  description?: string;
  connection_details: EntityConnectionDetails;
  tag_ids?: string[];
  tag_details?: TagDetail[];
  last_sync?: string;
  sync_status?: SyncStatus;
  settings?: EntitySettings;
  health_metrics?: EntityHealthMetrics;
  created_at?: string;
  updated_at?: string;
  base_currency?: string; // Added – some endpoints return org currency here
  country_code?: string;  // Country of organization (used to infer currency)
  // Financial year information for date filtering
  financial_year_end_day?: number;
  financial_year_end_month?: number;
  financial_year_start_date?: string;
  // Enhanced sync tracking properties for sync status polling
  activeSyncJobId?: string;
  syncJobStatus?: SyncJobStatus;
}

export interface EntitySummary {
  entity_id: string;
  entity_name: string;
  type: EntityType;
  status: EntityStatus;
  connection_status: EntityStatus;
  last_sync?: string;
  error_message?: string;
  pending_items_count?: number;
  error_count?: number;
  health_score?: number;
  sync_status?: SyncStatus;
  pending_schedules_count?: number;
  tagDetails?: TagDetail[];
  // Enhanced sync tracking
  activeSyncJobId?: string;
  syncJobStatus?: SyncJobStatus;
}

// Pagination information (used across multiple responses)
export interface PaginationInfo {
  current_page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next?: boolean;
  has_previous?: boolean;
}

export interface EntityListResponse {
  entities: EntitySummary[];
  pagination?: PaginationInfo;
  credit_info?: {
    credit_balance: number;
    credits_used_total: number;
    credits_used_llm: number;
    last_credit_usage?: string;
  };
}

// Wizard Step Types for Entity Creation
export interface EntityWizardStep1 {
  entity_name: string;
  type: EntityType;
  description?: string;
}

export interface EntityWizardStep2 {
  connection_method: 'oauth' | 'manual';
  oauth_provider?: 'xero' | 'qbo';
  manual_config?: {
    api_key?: string;
    secret?: string;
    endpoint?: string;
  };
}

export interface EntityWizardStep3 {
  settings: Partial<EntitySettings>;
  auto_sync_enabled: boolean;
  sync_frequency: 'hourly' | 'daily' | 'weekly' | 'manual';
}

// OAuth Flow Types
export interface OAuthInitiateResponse {
  authorization_url: string;
  state: string;
  code_verifier?: string;
}

export interface OAuthCallbackData {
  code: string;
  state: string;
  scope?: string;
  error?: string;
  error_description?: string;
}

export interface OAuthTokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  scope?: string;
}

// Connection Management Types
export interface ConnectionTestResult {
  success: boolean;
  message: string;
  details?: {
    organization_name?: string;
    organization_id?: string;
    permissions?: string[];
    api_version?: string;
  };
  error_details?: {
    error_code?: string;
    error_message?: string;
    suggested_action?: string;
  };
}

export interface SyncStatus {
  is_syncing: boolean;
  current_step?: string;
  progress_percentage?: number; // 0-100
  estimated_remaining?: string;
  user_message?: string;
  last_sync_completed?: string;
  sync_duration_warning?: string;
}

// Enhanced sync types for high-performance backend integration
export interface SyncTriggerResponse {
  message: string;
  syncJobId: string;
  status: 'initiated';
  progressEndpoint: string;
}

export interface SyncJobStatus {
  syncJobId: string;
  entityId: string;
  status: 'processing' | 'completed' | 'failed';
  progressPercent: number; // 0-100
  stage: 'bills' | 'attachments' | 'completion';
  startedAt: string; // ISO date
  errorDetails?: Record<string, any>;
  durationSeconds?: number;
}

export interface SyncConflictError {
  error: 'sync_in_progress';
  message: string;
  syncJobId: string;
  progressPercent: number;
  startedAt: string;
  currentStage: string;
  estimatedRemaining: string;
}

// Dashboard Types
export interface EntityDashboardData {
  entity: EntityResponse;
  health_metrics: EntityHealthMetrics;
  sync_status: SyncStatus;
  recent_activity: ActivityItem[];
  data_summary: {
    accounts_count: number;
    transactions_count: number;
    invoices_count: number;
    last_updated: string;
  };
}

export interface ActivityItem {
  id: string;
  type: 'sync' | 'error' | 'connection' | 'settings' | 'data';
  title: string;
  description: string;
  timestamp: string;
  status: 'success' | 'error' | 'warning' | 'info';
  details?: Record<string, any>;
}

// Filter and Search Types
export interface EntityFilters {
  page?: number;
  limit?: number;
  clientId?: string;
  type?: EntityType;
  status?: EntityStatus;
  connectionStatus?: EntityStatus;
  search?: string;
  tagIds?: string[];
  includeTagDetails?: boolean;
  sortBy?: 'name' | 'createdAt' | 'lastSync' | 'healthScore';
  sortOrder?: 'asc' | 'desc';
}

// Enum Options for UI
export interface EnumOption {
  value: string;
  label: string;
  description?: string;
  icon?: string;
}

export interface EntityEnums {
  entity_types: EnumOption[];
  entity_statuses: EnumOption[];
  sync_frequencies: EnumOption[];
  currencies: Array<{ value: string; label: string; symbol: string; }>;
  amortization_periods: Array<{ value: number; label: string; }>;
  sync_data_types: Array<{ value: string; label: string; description: string; }>;
}

// API Response Types
export interface CreateEntityResponse {
  message: string;
  entity_id: string;
  entity_name: string;
}

export interface UpdateEntityResponse {
  message: string;
}

export interface DeleteEntityResponse {
  message: string;
}

export interface InitiateConnectionResponse {
  authorization_url: string;
  state: string;
}

export interface CompleteConnectionResponse {
  message: string;
  entity_id: string;
  connection_status: EntityStatus;
}

// =================================================================
// TAG MANAGEMENT TYPES
// =================================================================

// Tag data structures
export interface Tag {
  tagId: string;
  name: string;
  color: string;
  description?: string;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

// Union type for handling backend/frontend field name differences
export interface BackendTagDetail {
  tag_id: string;
  name: string;
  color: string;
  description?: string;
}

export interface FrontendTagDetail {
  tagId: string;
  name: string;
  color: string;
  description?: string;
}

export interface TagDetail {
  tagId: string;
  name: string;
  color: string;
  description?: string;
}

// Type guard for checking tag format
export function isBackendTag(tag: any): tag is BackendTagDetail {
  return typeof tag === 'object' && tag !== null && 'tag_id' in tag;
}

export function isFrontendTag(tag: any): tag is FrontendTagDetail {
  return typeof tag === 'object' && tag !== null && 'tagId' in tag;
}

// Utility to normalize tag format
export function normalizeTag(tag: BackendTagDetail | FrontendTagDetail): TagDetail {
  if (isBackendTag(tag)) {
    return {
      tagId: tag.tag_id,
      name: tag.name,
      color: tag.color,
      description: tag.description
    };
  }
  return tag as TagDetail;
}

// Tag request types
export interface TagCreateRequest {
  name: string;
  color?: string;
  description?: string;
}

export interface TagUpdateRequest {
  name?: string;
  color?: string;
  description?: string;
}

// Tag response types
export interface TagResponse {
  tagId: string;
  name: string;
  color: string;
  description?: string;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface TagListResponse {
  tags: TagResponse[];
  total: number;
  pagination?: PaginationInfo;
}

export interface TagDeleteResponse {
  message: string;
  affectedEntities: number;
}

// Tag filtering and search
export interface TagFilters {
  firmId?: string; // Required for firm-scoped tag operations
  includeUnused?: boolean;
  sortBy?: 'name' | 'usageCount' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

// Bulk tag operations
export interface TagBulkUpdateRequest {
  entityIds: string[];
  addTagIds?: string[];
  removeTagIds?: string[];
}

export interface TagBulkUpdateResponse {
  message: string;
  updatedEntities: number;
  errors?: Array<{
    entityId: string;
    error: string;
  }>;
}

// Tag UI state
export interface TagState {
  selectedTags: string[];
  availableTags: TagResponse[];
  loading: boolean;
  error?: string;
}


// Tag assignment component props
export interface TagAssignmentProps {
  entityId: string;
  currentTagIds: string[];
  onTagsChange: (tagIds: string[]) => void;
  disabled?: boolean;
}

// Tag display component props
export interface TagDisplayProps {
  tags: TagDetail[];
  maxDisplay?: number;
  size?: 'sm' | 'md' | 'lg';
  onRemoveTag?: (tagId: string) => void;
  readOnly?: boolean;
}

// Tag selector props
export interface TagSelectorProps {
  selectedTagIds: string[];
  onTagsChange: (tagIds: string[]) => void;
  placeholder?: string;
  maxTags?: number;
  disabled?: boolean;
}

// Bulk tag assignment component props
export interface TagBulkAssignmentProps {
  entityIds: string[];
  availableTags: TagResponse[];
  currentTagIds: string[];
  onBulkUpdate: (request: TagBulkUpdateRequest) => Promise<void>;
  maxEntities?: number;
  disabled?: boolean;
}

// Tag management page state
export interface TagManagementState {
  tags: TagResponse[];
  filteredTags: TagResponse[];
  loading: boolean;
  error?: string;
  filters: TagFilters;
  pagination?: PaginationInfo;
  editingTag?: TagResponse;
  deletingTag?: TagResponse;
}

// Color picker options
export interface TagColorOption {
  value: string;
  label: string;
  preview: string;
}

// Common tag colors
export const TAG_COLORS: TagColorOption[] = [
  { value: '#6b7280', label: 'Gray', preview: 'bg-gray-500' },
  { value: '#ef4444', label: 'Red', preview: 'bg-red-500' },
  { value: '#f97316', label: 'Orange', preview: 'bg-orange-500' },
  { value: '#eab308', label: 'Yellow', preview: 'bg-yellow-500' },
  { value: '#22c55e', label: 'Green', preview: 'bg-green-500' },
  { value: '#3b82f6', label: 'Blue', preview: 'bg-blue-500' },
  { value: '#8b5cf6', label: 'Purple', preview: 'bg-purple-500' },
  { value: '#ec4899', label: 'Pink', preview: 'bg-pink-500' },
  { value: '#06b6d4', label: 'Cyan', preview: 'bg-cyan-500' },
  { value: '#84cc16', label: 'Lime', preview: 'bg-lime-500' }
];

// Tag validation constants
export const TAG_CONSTRAINTS = {
  MAX_NAME_LENGTH: 50,
  MAX_DESCRIPTION_LENGTH: 200,
  MAX_TAGS_PER_ENTITY: 10,
  MIN_NAME_LENGTH: 1
} as const;

// Tag-related error types
export interface TagError {
  type: 'validation' | 'network' | 'conflict' | 'notFound';
  message: string;
  field?: string;
}

// Tag analytics (for future features)
export interface TagAnalytics {
  tagId: string;
  name: string;
  usageCount: number;
  trend: 'increasing' | 'decreasing' | 'stable';
  entitiesUsing: EntitySummary[];
  createdDate: string;
  lastUsed?: string;
} 